import { useRef } from 'react';
import { authAPI } from '../services/api';
import { useToast } from '../components/ToastContainer';

interface Message {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'announcement';
  title?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  expiresAt?: string;
  sentBy?: {
    userId: number;
    userEmail: string;
    userName?: string;
  };
  metadata?: Record<string, any>;
}

/**
 * Hook to process messages from keep-alive/status responses
 * This piggybacks on existing calls instead of making separate polling requests
 */
export const useMessagePolling = () => {
  const { showToast } = useToast();
  // Track messages that have been shown recently (to avoid spam)
  // Map: messageId -> timestamp when last shown
  const recentlyShownMessages = useRef<Map<string, number>>(new Map());
  // Track messages that have been permanently acknowledged (user dismissed them)
  const acknowledgedMessages = useRef<Set<string>>(new Set());
  
  // Don't show the same message again within this time window (in milliseconds)
  const SHOW_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Process messages from a response (called when keep-alive or status returns messages)
   */
  const processMessages = async (messages: Message[] | undefined) => {
    if (!messages || messages.length === 0) {
      return;
    }

    const now = Date.now();
    
    // Filter out acknowledged messages and messages shown recently
    const messagesToShow = messages.filter(msg => {
      // Skip if permanently acknowledged
      if (acknowledgedMessages.current.has(msg.id)) {
        return false;
      }
      
      // Skip if shown recently (within cooldown period)
      const lastShown = recentlyShownMessages.current.get(msg.id);
      if (lastShown && (now - lastShown) < SHOW_COOLDOWN_MS) {
        return false;
      }
      
      return true;
    });

    // Display messages as toasts
    for (const message of messagesToShow) {
      // Map message type to toast type
      const toastType = message.type === 'announcement' ? 'info' : message.type;
      
      // Create message text with title if available
      const messageText = message.title 
        ? `${message.title}: ${message.message}`
        : message.message;

      // Show toast with longer duration based on priority
      // Increased durations to give users more time to read
      const duration = message.priority === 'urgent' ? 15000 
        : message.priority === 'high' ? 12000 
        : message.priority === 'medium' ? 10000 
        : 8000;

      showToast(messageText, toastType, duration);

      // Track that we've shown this message (but don't acknowledge it permanently)
      // This allows the message to be shown again after the cooldown period
      recentlyShownMessages.current.set(message.id, now);
      
      // Clean up old entries from the map (older than 1 hour)
      const oneHourAgo = now - (60 * 60 * 1000);
      const entriesToDelete: string[] = [];
      recentlyShownMessages.current.forEach((timestamp, msgId) => {
        if (timestamp < oneHourAgo) {
          entriesToDelete.push(msgId);
        }
      });
      entriesToDelete.forEach(msgId => {
        recentlyShownMessages.current.delete(msgId);
      });
    }
  };

  /**
   * Manually acknowledge a message (e.g., when user dismisses it)
   * This permanently removes it from being shown again
   */
  const acknowledgeMessage = async (messageId: string) => {
    try {
      await authAPI.acknowledgeMessage(messageId);
      acknowledgedMessages.current.add(messageId);
      recentlyShownMessages.current.delete(messageId);
    } catch (error) {
      console.error('Error acknowledging message:', error);
    }
  };

  return {
    processMessages,
    acknowledgeMessage,
    acknowledgedMessages: acknowledgedMessages.current
  };
};

